import { motion } from "framer-motion";

/**
 * GoldenQuill - A floating golden feather/quill with dust particles
 * Used in the Name input step to add mystical atmosphere
 */
export const GoldenQuill = ({ className = "" }) => {
    // Generate dust particles
    const dustParticles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        delay: i * 0.3,
        duration: 2 + Math.random() * 2,
        x: -10 + Math.random() * 20,
        y: 20 + Math.random() * 30
    }));

    return (
        <div className={`relative w-32 h-40 ${className}`}>
            {/* Golden dust particles */}
            {dustParticles.map((particle) => (
                <motion.div
                    key={particle.id}
                    className="absolute w-1 h-1 rounded-full"
                    style={{
                        background: "radial-gradient(circle, #FFD700 0%, #D4AF37 50%, transparent 100%)",
                        left: "50%",
                        bottom: "20%",
                        filter: "blur(0.5px)"
                    }}
                    initial={{ 
                        opacity: 0, 
                        x: 0, 
                        y: 0,
                        scale: 0.5
                    }}
                    animate={{
                        opacity: [0, 0.8, 0],
                        x: particle.x,
                        y: -particle.y,
                        scale: [0.5, 1, 0.3]
                    }}
                    transition={{
                        duration: particle.duration,
                        delay: particle.delay,
                        repeat: Infinity,
                        ease: "easeOut"
                    }}
                />
            ))}

            {/* Main quill container with floating animation */}
            <motion.div
                className="absolute inset-0 flex items-center justify-center"
                animate={{
                    y: [-5, 5, -5],
                    rotate: [-2, 2, -2]
                }}
                transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            >
                {/* Quill glow */}
                <motion.div
                    className="absolute w-24 h-32 rounded-full"
                    style={{
                        background: "radial-gradient(ellipse, rgba(212,175,55,0.15) 0%, transparent 70%)",
                        filter: "blur(10px)"
                    }}
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3]
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />

                {/* Quill SVG */}
                <svg
                    viewBox="0 0 60 120"
                    className="w-16 h-32 sm:w-20 sm:h-40"
                    style={{
                        filter: "drop-shadow(0 0 8px rgba(212,175,55,0.5))"
                    }}
                >
                    <defs>
                        <linearGradient id="quillGold" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#FFD700" />
                            <stop offset="30%" stopColor="#D4AF37" />
                            <stop offset="70%" stopColor="#B8960C" />
                            <stop offset="100%" stopColor="#D4AF37" />
                        </linearGradient>
                        <linearGradient id="quillShaft" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#D4AF37" />
                            <stop offset="50%" stopColor="#8B7355" />
                            <stop offset="100%" stopColor="#4A3728" />
                        </linearGradient>
                    </defs>

                    {/* Feather vanes - left side */}
                    <path
                        d="M28 10 Q15 20 12 35 Q10 50 15 65 Q20 75 28 80"
                        fill="none"
                        stroke="url(#quillGold)"
                        strokeWidth="0.5"
                        opacity="0.6"
                    />
                    <path
                        d="M28 15 Q18 25 16 38 Q14 52 18 62 Q22 70 28 75"
                        fill="url(#quillGold)"
                        opacity="0.8"
                    />
                    
                    {/* Feather vanes - right side */}
                    <path
                        d="M32 10 Q45 20 48 35 Q50 50 45 65 Q40 75 32 80"
                        fill="none"
                        stroke="url(#quillGold)"
                        strokeWidth="0.5"
                        opacity="0.6"
                    />
                    <path
                        d="M32 15 Q42 25 44 38 Q46 52 42 62 Q38 70 32 75"
                        fill="url(#quillGold)"
                        opacity="0.8"
                    />

                    {/* Central shaft */}
                    <path
                        d="M30 5 L30 115"
                        stroke="url(#quillShaft)"
                        strokeWidth="3"
                        strokeLinecap="round"
                    />
                    
                    {/* Nib/tip */}
                    <path
                        d="M30 105 L27 118 L30 115 L33 118 L30 105"
                        fill="#D4AF37"
                    />
                    
                    {/* Feather barbs - fine details */}
                    {[20, 30, 40, 50, 60].map((y) => (
                        <g key={y}>
                            <line
                                x1="30" y1={y}
                                x2={15 + (y / 10)} y2={y + 5}
                                stroke="#D4AF37"
                                strokeWidth="0.3"
                                opacity="0.4"
                            />
                            <line
                                x1="30" y1={y}
                                x2={45 - (y / 10)} y2={y + 5}
                                stroke="#D4AF37"
                                strokeWidth="0.3"
                                opacity="0.4"
                            />
                        </g>
                    ))}
                </svg>
            </motion.div>

            {/* Ink drip animation at tip */}
            <motion.div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary"
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    y: [0, 15, 30]
                }}
                transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 2,
                    ease: "easeIn"
                }}
            />
        </div>
    );
};

export default GoldenQuill;
